import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { OligoForm } from 'src/models';

import { isJapanUser } from 'src/utils';
import { submitAddToCart as submitAddToCartAction } from './actions';
import {
  getRequiredValidator,
  getSenseValidators,
  maxLenValidator,
  nameValidator,
  OLIGO_NAME_MAX,
  RESEARCHER_NAME_MAX,
  startsWithSpaceValidator,
} from './validators';
import {
  OLIGO_NAME,
  RESEARCHER_NAME,
  RNA_TYPE,
  RNA_OPTIONS,
  SENSE_SEQUENCE,
  ANTISENSE_SEQUENCE,
} from './constants';
import { isAddToCartAvailable, getAntiseseSequence, validateSenseSequence } from './helpers';
import messages from './messages';
import OligosFormComponent from './OligosFormComponent';

const OligosFormContainer = ({ submitAddToCart, stealth }) => {
  const [formData, setFormData] = React.useState(OligoForm.getDefaultFormState());
  const [updatedSequenceValue, setUpdatedSequenceValue] = React.useState('');
  const [rnaTypeChanged, setRNATypeChanged] = React.useState(false);
  const isJapan = isJapanUser();

  const onChangeField = (fieldName, value, isValid) => {
    if (fieldName === RNA_TYPE) {
      setUpdatedSequenceValue(formData[SENSE_SEQUENCE]?.value);
      setRNATypeChanged(!rnaTypeChanged);

      setFormData((prevState) => ({
        ...prevState,
        [SENSE_SEQUENCE]: { value: '', isValid: false },
        [fieldName]: { ...prevState[fieldName], value, isValid },
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [fieldName]: { ...prevState[fieldName], value, isValid },
      }));
    }
  };

  React.useEffect(() => {
    if (updatedSequenceValue) {
      const isValid = validateSenseSequence(formData[RNA_TYPE]?.value, updatedSequenceValue);

      setFormData({
        ...formData,
        [SENSE_SEQUENCE]: { value: updatedSequenceValue, isValid }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rnaTypeChanged, updatedSequenceValue]);

  React.useEffect(() => {
    if (stealth) {
      const data = OligoForm.getDefaultFormState();

      setFormData({ ...data, [RNA_TYPE]: { value: RNA_OPTIONS.STEALTH_RNAI, isValid: true } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <OligosFormComponent
      {...{
        isJapan,
        onChangeField,
        formData,
        onChangeSenseSequence: (value, e, canValidate, isValid) => {
          onChangeField(SENSE_SEQUENCE, value.toUpperCase(), isValid);
          onChangeField(ANTISENSE_SEQUENCE, getAntiseseSequence(value), true);
        },
        validators: {
          [OLIGO_NAME]: [
            getRequiredValidator(messages.oligoNameRequired),
            nameValidator(messages.nameValidation),
            maxLenValidator(OLIGO_NAME_MAX, messages.oligoNameMaxLengthError),
            startsWithSpaceValidator(messages.oligoNameStartsWithSpaceError),
          ],
          [RESEARCHER_NAME]: [
            getRequiredValidator(messages.researcherNameRequired),
            nameValidator(messages.nameValidation),
            maxLenValidator(RESEARCHER_NAME_MAX, messages.researcherNameMaxLengthError),
            startsWithSpaceValidator(messages.researcherNameStartsWithSpaceError),
          ],
          [SENSE_SEQUENCE]: getSenseValidators(formData[RNA_TYPE].value),
        },
        onAddToCart: () => submitAddToCart(OligoForm.processApiData(formData)),
        addToCartAvailable: isAddToCartAvailable(formData),
        isSiRNASelected: formData[RNA_TYPE]?.value === RNA_OPTIONS.SI_RNA,
      }}
    />
  );
};

OligosFormContainer.propTypes = {
  submitAddToCart: PropTypes.func.isRequired,
  stealth: PropTypes.bool
};

const mapDispatchToProps = ({
  submitAddToCart: submitAddToCartAction,
});

export default connect(null, mapDispatchToProps)(OligosFormContainer);
